// Read-only state handle for E2E tests (and debugging). Not used by the app.
// Kept out of main.ts so the entry file stays focused (and under the size cap):
// tests read `window.__pianoTutor`; `feedNote` injects MIDI through the command
// layer without a real device.

interface StaffSnapshotView {
  enabled: boolean;
  split: number;
  rows: number;
  follow: string;
}
interface StaffSnapshot extends StaffSnapshotView {
  /** Notehead states currently visible, e.g. ["future","hit"]. */
  noteStates: string[];
  /** Note-values currently visible, e.g. ["quarter","half"]; "none" = unclassified. */
  noteValues: string[];
  nowMarkerX: number | null;
  /** Number of measure barlines currently drawn. */
  barlineCount: number;
  /** Number of beams (grouped eighth/sixteenth runs) currently drawn. */
  beamCount: number;
}
/** Write patch for `setStaffView` — keys mirror the DisplayOptions staff fields. */
interface StaffViewPatch {
  staffEnabled?: boolean;
  staffSplit?: number;
  staffRows?: number;
  staffFollow?: string;
}

interface DebugAccessors {
  playhead(): number | null;
  playing(): boolean;
  mode(): string | null;
  /** The engine's live tempo multiplier — the Coach ramps this for a slow drill, so it is
   *  not always the tempo control's value. Null with no song. */
  tempo(): number | null;
  misses(): number;
  handCounts(): { left: number; right: number; unknown: number };
  coachStatus(): string | null;
  countingIn(): boolean;
  demoActive(): boolean;
  commandMode(): boolean;
  feedNote(pitch: number, on: boolean, timeMs: number): void;
  staff(): StaffSnapshot | null;
  setStaffView(patch: StaffViewPatch): void;
  /** Recent intended MIDI-out sends (`on:60`/`off:60`/`panic`) — F1 E2E seam. */
  midiOut(): string[];
  /** Metronome diagnostics: AudioContext state + why it might be silent. */
  audio(): {
    state: string;
    enabled: boolean;
    freeRun: boolean;
    mode: string | null;
    playing: boolean;
  };
  /** Force one accented metronome click (resumes audio first) — bench output test. */
  beep(): void;
  /** Performance-recorder state (E2E seam for MIDI export). */
  recording(): boolean;
  recordedNotes(): number;
}

export function installDebugHandle(a: DebugAccessors): void {
  (
    window as unknown as {
      __pianoTutor?: {
        readonly playhead: number | null;
        readonly playing: boolean;
        readonly mode: string | null;
        readonly tempo: number | null;
        readonly misses: number;
        readonly handCounts: { left: number; right: number; unknown: number };
        readonly coachStatus: string | null;
        readonly countingIn: boolean;
        readonly demoActive: boolean;
        readonly commandMode: boolean;
        feedNote(pitch: number, on: boolean, timeMs: number): void;
        readonly staff: StaffSnapshot | null;
        setStaffView(patch: StaffViewPatch): void;
        readonly midiOut: string[];
        audio(): {
          state: string;
          enabled: boolean;
          freeRun: boolean;
          mode: string | null;
          playing: boolean;
        };
        beep(): void;
        readonly recording: boolean;
        readonly recordedNotes: number;
      };
    }
  ).__pianoTutor = {
    get playhead() {
      return a.playhead();
    },
    get playing() {
      return a.playing();
    },
    get mode() {
      return a.mode();
    },
    get tempo() {
      return a.tempo();
    },
    get misses() {
      return a.misses();
    },
    get handCounts() {
      return a.handCounts();
    },
    get coachStatus() {
      return a.coachStatus();
    },
    get countingIn() {
      return a.countingIn();
    },
    get demoActive() {
      return a.demoActive();
    },
    get commandMode() {
      return a.commandMode();
    },
    feedNote: a.feedNote,
    get staff() {
      return a.staff();
    },
    setStaffView: a.setStaffView,
    get midiOut() {
      return a.midiOut();
    },
    audio: a.audio,
    beep: a.beep,
    get recording() {
      return a.recording();
    },
    get recordedNotes() {
      return a.recordedNotes();
    },
  };
}
